/**
 * Shared model-alias option definitions for the AI Center Testing tab.
 *
 * The server-side source of truth is `AI_MODEL_ALIASES` in
 * `apps/api/src/services/ai-config-store.ts`. This module mirrors the
 * chat-capable subset (excluding `embedding`, which is not a chat alias) with
 * descriptive labels, so the Playground console and the Evaluations runner
 * present the same choices with the same wording.
 *
 * Labels follow the more descriptive Evaluations style — less-frequent admin
 * users benefit from seeing the provider/model hint inline rather than a bare
 * alias key.
 *
 * The `GET /admin/ai/providers` endpoint returns `aliases` as a
 * `Record<alias, "provider:model">` map; `aliasLabelMap` below mirrors the
 * keys so a UI can resolve a raw alias back to its label without a second
 * request.
 */

export interface AliasOption {
  /** Raw alias key — matches `AI_MODEL_ALIASES` server-side. */
  value: string
  /** Descriptive label shown in dropdowns. */
  label: string
}

/**
 * Chat-capable model aliases, ordered roughly cheapest→premium.
 *
 * `embedding` is intentionally absent — it is not a chat alias and the test
 * console / evaluation harness only issue chat completions.
 */
export const MODEL_ALIAS_OPTIONS: readonly AliasOption[] = [
  { value: 'fast_private', label: 'Fast / Private (local Ollama)' },
  { value: 'fast_cloud', label: 'Fast / Cloud (Groq Llama 3.3)' },
  { value: 'reasoning_premium', label: 'Reasoning / Premium (GPT-4o / Claude 3.5)' },
  { value: 'reasoning_claude', label: 'Reasoning / Claude (Anthropic)' },
  { value: 'multimodal', label: 'Multimodal (Gemini 2.0 Flash)' },
] as const

/**
 * Sentinel value meaning "let the task's default alias apply" — used by the
 * Playground console where an explicit override is optional. The Evaluations
 * runner always requires a concrete alias, so it does not include this entry.
 */
export const TASK_DEFAULT_ALIAS = ''

/** Dropdown options including the "use task default" sentinel. */
export const MODEL_ALIAS_OPTIONS_WITH_DEFAULT: readonly AliasOption[] = [
  { value: TASK_DEFAULT_ALIAS, label: 'Use the task default' },
  ...MODEL_ALIAS_OPTIONS,
] as const

/** Resolve a raw alias value to its descriptive label (fallback to the raw value). */
export function aliasLabel(value: string): string {
  return MODEL_ALIAS_OPTIONS.find((o) => o.value === value)?.label ?? value
}
